import React, { type FunctionComponent, useEffect, useMemo, useState } from 'react'
import { Select } from '@retailer-platform/shared-components'
import { useCallback } from 'react'
import { type Styles } from 'react-select'
import { useDashMessage, useDashMessages } from 'intl/intl.hooks'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'

interface LocaleOption {
  label: string
  localeCode: string
  localeId: string
}

export type OnLocaleChangeCallback = (locale: LocaleOption) => void

interface Props {
  isDisabled?: boolean
  onChange: OnLocaleChangeCallback
  // Use initialValue for uncontrolled behavior
  initialValue?: string
  // Use value for controlled behavior
  value?: string
  isModernTheme?: boolean
  locales: Omit<LocaleOption, 'label'>[]
  styles?: Styles
}

const iconMapping = {
  US: '🇺🇸',
  CA: '🇨🇦',
  AU: '🇦🇺',
  UK: '🇬🇧',
  default: '',
}

export const LocaleSelect: FunctionComponent<Props> = ({ locales, ...props }) => {
  const placeholder = useDashMessage('components.localeSelect.noLocale')
  const [value, setValue] = useState<LocaleOption>(null)

  const languageLabels = useDashMessages({
    EN: 'components.localeSelect.locales.en',
    FR: 'components.localeSelect.locales.fr',
  })

  const localesOptions: LocaleOption[] = useMemo(
    () =>
      locales.map(locale => {
        const [languageCode, countryCode] = locale.localeCode.toUpperCase().split('_')

        const label = [iconMapping[countryCode] || iconMapping.default]

        if (languageCode in languageLabels) {
          label.push(languageLabels[languageCode])
        } else {
          // if for some reason the languageCode is not recognized, print the localeCode.
          label.push(locale.localeCode.toUpperCase())
        }

        return { label: label.join(' '), value: locale.localeId, ...locale }
      }),
    [locales, languageLabels]
  )

  const findLocaleByCodeOrId = useCallback(
    value => localesOptions.find(locale => [locale.localeCode, locale.localeId].includes(value)),
    [localesOptions]
  )
  /**
   * Initial value setting, will try to match either the localeCode or the localeId.
   * This is useful because we don't know which kind of data the consumer will be
   * using.
   */
  useEffect(
    () => {
      if (!props.initialValue) return

      const found = findLocaleByCodeOrId(props.initialValue)

      setValue(found)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (props.initialValue) return

    const found = findLocaleByCodeOrId(props.value)

    setValue(found)
  }, [setValue, findLocaleByCodeOrId, props.value, props.initialValue])

  const onChange: Props['onChange'] = option => {
    setValue(option)

    if (props.onChange) props.onChange(option)
  }

  return (
    <Select<LocaleOption>
      {...props}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      options={localesOptions}
      aria-label="locale-select"
    />
  )
}

export const RetailerLocaleSelect: FunctionComponent<Omit<Props, 'locales'>> = props => {
  const { warehouse } = useWarehouseContext()

  return <LocaleSelect locales={warehouse.locales} {...props} />
}
